import { Component, OnInit } from '@angular/core';
import { FormBuilder, Validators, UntypedFormGroup } from '@angular/forms';

import { BeerProperties as BeerProperty } from '@products/interfaces/beer-properties';
import { SubformBaseComponent } from '@shared/components/subform-base/subform-base.component';
import { sugarContentValidators } from '@shared/validators/sugar-content.validators';

interface HasSugarContentProperties {
  containsAddedSugar?: boolean;
  sugarContentType?: BeerProperty.SugarContentTypeEnum;
  containsAddedSweetener?: boolean;
  sugarContentInGramsPerHundredMilliliters?: number;
}

export interface RequiredExceptions {
  containsAddedSugar?: boolean;
  sugarContentType?: boolean;
  containsAddedSweetener?: boolean;
  skipSugarContentInGramsPerHundredMilliliters?: boolean;
}

@Component({
  selector: 'app-subform-base-with-sugar-content-properties',
  template: ``,
  styles: [],
})
export abstract class SubformBaseWithSugarContentPropertiesComponent
  extends SubformBaseComponent
  implements OnInit
{
  sugarContentType = BeerProperty.SugarContentTypeEnum;
  alcoholic: boolean = false;

  alcoholicSugarContentType = [
    {
      name: this.translateService.instant(
        'PRODUCTS.SUGAR_CONTENT_TYPE.' + this.sugarContentType.SugarFree
      ),
      value: this.sugarContentType.SugarFree,
    },
  ];
  nonAlcoholicSugarContentType = [
    {
      name: this.translateService.instant(
        'PRODUCTS.SUGAR_CONTENT_TYPE.' + this.sugarContentType.SugarFree
      ),
      value: this.sugarContentType.SugarFree,
    },
  ];

  CONTAINS_ADDED_SUGAR = 'PRODUCTS.SUGAR_CONTENT_FIELDS.CONTAINS_ADDED_SUGAR';
  CONTAINS_ADDED_SWEETENER =
    'PRODUCTS.SUGAR_CONTENT_FIELDS.CONTAINS_ADDED_SWEETENER';
  SUGAR_CONTENT_IN_GRAMS_PER_HUNDRED_MILLILITERS =
    'PRODUCTS.SUGAR_CONTENT_FIELDS.SUGAR_CONTENT_IN_GRAMS_PER_HUNDRED_MILLILITERS';
  SUGAR_CONTENT_TYPE = 'PRODUCTS.SUGAR_CONTENT_FIELDS.SUGAR_CONTENT_TYPE';

  override ngOnInit(): void {
    super.ngOnInit();
    if (this.alcoholic) {
      this.setSugarContentOptionsAlcoholic(
        this.group.value.sugarContentProperties
      );
    } else {
      this.setSugarContentOptionsNonAlcoholic(this.group.value);
    }
  }

  static buildFields(
    data?: HasSugarContentProperties,
    exceptions?: RequiredExceptions,
    alcoholic?: boolean,
    fb?: FormBuilder
  ) {
    if (!alcoholic) {
      return {
        containsAddedSugar: [data?.containsAddedSugar ?? false],

        sugarContentType: [
          data?.sugarContentType ?? BeerProperty.SugarContentTypeEnum.SugarFree,
          exceptions?.sugarContentType ?? Validators.required,
        ],

        containsAddedSweetener: [data?.containsAddedSweetener ?? false],

        ...((data?.containsAddedSugar || data?.containsAddedSweetener) &&
          !exceptions?.skipSugarContentInGramsPerHundredMilliliters && {
            sugarContentInGramsPerHundredMilliliters: [
              data?.sugarContentInGramsPerHundredMilliliters,
              sugarContentValidators,
            ],
          }),
      };
    } else {
      return fb?.group({
        containsAddedSugar: [data?.containsAddedSugar ?? false],

        sugarContentType: [
          data?.sugarContentType ??
            (exceptions?.sugarContentType
              ? null
              : BeerProperty.SugarContentTypeEnum.SugarFree),
          exceptions?.sugarContentType ? null : Validators.required,
        ],

        containsAddedSweetener: [data?.containsAddedSweetener ?? false],

        ...((data?.containsAddedSugar || data?.containsAddedSweetener) && {
          sugarContentInGramsPerHundredMilliliters: [
            data?.sugarContentInGramsPerHundredMilliliters,
          ],
        }),
      });
    }
  }
  /*
   * sugarContentInGramsPerHundredMilliliters field is required if
   * containsAddedSugar or containsAddedSweetener is selected
   */
  onSweetenerOrSugarChangedAlcoholic() {
    const tmpGroup: UntypedFormGroup = this.group.get(
      'sugarContentProperties'
    ) as UntypedFormGroup;
    if (
      this.getCtrl('sugarContentProperties.containsAddedSugar')?.value ||
      this.getCtrl('sugarContentProperties.containsAddedSweetener')?.value
    ) {
      tmpGroup?.addControl(
        'sugarContentInGramsPerHundredMilliliters',
        this.fb.control(null, sugarContentValidators)
      );

      if (this.getCtrl('sugarContentProperties.containsAddedSugar')?.value) {
        this.alcoholicSugarContentType = [
          {
            name: this.translateService.instant(
              'PRODUCTS.SUGAR_CONTENT_TYPE.' + this.sugarContentType.Light
            ),
            value: this.sugarContentType.Light,
          },
          {
            name: this.translateService.instant(
              'PRODUCTS.SUGAR_CONTENT_TYPE.' + this.sugarContentType.Normal
            ),
            value: this.sugarContentType.Normal,
          },
        ];
      } else {
        this.alcoholicSugarContentType = [
          {
            name: this.translateService.instant(
              'PRODUCTS.SUGAR_CONTENT_TYPE.' + this.sugarContentType.SugarFree
            ),
            value: this.sugarContentType.SugarFree,
          },
          {
            name: this.translateService.instant(
              'PRODUCTS.SUGAR_CONTENT_TYPE.' + this.sugarContentType.Light
            ),
            value: this.sugarContentType.Light,
          },
        ];
      }
      this.getCtrl('sugarContentProperties.sugarContentType')?.setValue(null);
    } else {
      tmpGroup?.removeControl('sugarContentInGramsPerHundredMilliliters');
      this.alcoholicSugarContentType = [
        {
          name: this.translateService.instant(
            'PRODUCTS.SUGAR_CONTENT_TYPE.' + this.sugarContentType.SugarFree
          ),
          value: this.sugarContentType.SugarFree,
        },
      ];

      this.getCtrl('sugarContentProperties.sugarContentType')?.setValue(null);
    }
  }
  /*
   * sugarContentInGramsPerHundredMilliliters field is required if
   * containsAddedSugar or containsAddedSweetener is selected
   * except energy drink
   */
  onSweetenerOrSugarChangedNonAlcoholic(withoutControl?: boolean) {
    if (
      (this.getCtrl('containsAddedSugar')?.value ||
        this.getCtrl('containsAddedSweetener')?.value) &&
      !withoutControl
    ) {
      this.group.addControl(
        'sugarContentInGramsPerHundredMilliliters',
        this.fb.control(null, sugarContentValidators)
      );
    } else {
      this.group.removeControl('sugarContentInGramsPerHundredMilliliters');
    }

    if (this.getCtrl('containsAddedSugar')?.value) {
      this.nonAlcoholicSugarContentType = [
        {
          name: this.translateService.instant(
            'PRODUCTS.SUGAR_CONTENT_TYPE.' + this.sugarContentType.ContainsSugar
          ),
          value: this.sugarContentType.ContainsSugar,
        },
        {
          name: this.translateService.instant(
            'PRODUCTS.SUGAR_CONTENT_TYPE.' +
              this.sugarContentType.ContainsReducedSugar
          ),
          value: this.sugarContentType.ContainsReducedSugar,
        },
      ];
    } else if (this.getCtrl('containsAddedSweetener')?.value) {
      this.nonAlcoholicSugarContentType = [
        {
          name: this.translateService.instant(
            'PRODUCTS.SUGAR_CONTENT_TYPE.' + this.sugarContentType.SugarFree
          ),
          value: this.sugarContentType.SugarFree,
        },
        {
          name: this.translateService.instant(
            'PRODUCTS.SUGAR_CONTENT_TYPE.' + this.sugarContentType.Light
          ),
          value: this.sugarContentType.Light,
        },
      ];
    } else {
      this.nonAlcoholicSugarContentType = [
        {
          name: this.translateService.instant(
            'PRODUCTS.SUGAR_CONTENT_TYPE.' + this.sugarContentType.SugarFree
          ),
          value: this.sugarContentType.SugarFree,
        },
      ];

      this.getCtrl('sugarContentType')?.setValue(
        this.sugarContentType.SugarFree
      );
    }
  }

  setSugarContentOptionsAlcoholic(sugarContent: HasSugarContentProperties) {
    if (sugarContent.containsAddedSugar) {
      this.alcoholicSugarContentType = [
        {
          name: this.translateService.instant(
            'PRODUCTS.SUGAR_CONTENT_TYPE.' + this.sugarContentType.Light
          ),
          value: this.sugarContentType.Light,
        },
        {
          name: this.translateService.instant(
            'PRODUCTS.SUGAR_CONTENT_TYPE.' + this.sugarContentType.Normal
          ),
          value: this.sugarContentType.Normal,
        },
      ];
    } else if (sugarContent.containsAddedSweetener) {
      this.alcoholicSugarContentType = [
        {
          name: this.translateService.instant(
            'PRODUCTS.SUGAR_CONTENT_TYPE.' + this.sugarContentType.SugarFree
          ),
          value: this.sugarContentType.SugarFree,
        },
        {
          name: this.translateService.instant(
            'PRODUCTS.SUGAR_CONTENT_TYPE.' + this.sugarContentType.Light
          ),
          value: this.sugarContentType.Light,
        },
      ];
    } else {
      this.alcoholicSugarContentType = [
        {
          name: this.translateService.instant(
            'PRODUCTS.SUGAR_CONTENT_TYPE.' + this.sugarContentType.SugarFree
          ),
          value: this.sugarContentType.SugarFree,
        },
      ];
    }
  }
  setSugarContentOptionsNonAlcoholic(sugarContent: HasSugarContentProperties) {
    if (sugarContent.containsAddedSugar) {
      this.nonAlcoholicSugarContentType = [
        {
          name: this.translateService.instant(
            'PRODUCTS.SUGAR_CONTENT_TYPE.' + this.sugarContentType.ContainsSugar
          ),
          value: this.sugarContentType.ContainsSugar,
        },
        {
          name: this.translateService.instant(
            'PRODUCTS.SUGAR_CONTENT_TYPE.' +
              this.sugarContentType.ContainsReducedSugar
          ),
          value: this.sugarContentType.ContainsReducedSugar,
        },
      ];
    } else if (sugarContent.containsAddedSweetener) {
      this.nonAlcoholicSugarContentType = [
        {
          name: this.translateService.instant(
            'PRODUCTS.SUGAR_CONTENT_TYPE.' + this.sugarContentType.SugarFree
          ),
          value: this.sugarContentType.SugarFree,
        },
        {
          name: this.translateService.instant(
            'PRODUCTS.SUGAR_CONTENT_TYPE.' + this.sugarContentType.Light
          ),
          value: this.sugarContentType.Light,
        },
      ];
    } else {
      this.nonAlcoholicSugarContentType = [
        {
          name: this.translateService.instant(
            'PRODUCTS.SUGAR_CONTENT_TYPE.' + this.sugarContentType.SugarFree
          ),
          value: this.sugarContentType.SugarFree,
        },
      ];
    }
  }
}
